<template>
  <div :class="['buttons-container', `${SysTypeComputed}`]">
    <div
      v-for="(e, i) in buttonList"
      :key="e.id"
      :class="['main-button', { 'right-slot': e.isHasRightSlot }]"
    >
      <div
        :class="['google-shop-icon-box chxy-row-c-c', `${SysTypeComputed}`]"
        @click="handleDownload(e.funcType)"
      >
        <img
          v-if="e.isHasIcon"
          :src="getImageUrlWithCDN_TEST(`/download/${SysTypeComputed}.png`, 'local')"
          class="icon"
        />
        <div :class="['value-box', { checkbox: e.isHasCheckbox }]">
          <p class="value">{{ e.content }}</p>
          <div v-if="e.isHasCheckbox" class="checkbox-container">
            <van-checkbox
              v-model="e.checked"
              checked-color="#00bc2d"
              shape="square"
              icon-size="16px"
            ></van-checkbox>
            <span class="text">{{ e.checkedContent }}</span>
          </div>
        </div>
      </div>
      <div
        v-if="e.isHasRightSlot"
        :class="['right-slot', 'chxy-row-c-c', { link: i % 2 === 0 }]"
        @click="handleTutorial"
      >
        <div class="slot-content-box chxy-column">
          <SvgIcon
            name="download-guide_book"
            :fill="`var(--download-popup-icon_guide_book-link)`"
            :size="14"
          />
          <span class="text">{{ $t('DownloadPopup-components-InstallButtons.tutorial') }}</span>
        </div>
      </div>
      <div v-if="e.isHasTag" class="gift-icon-box">
        <span class="value">{{ e.tag }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useDevice } from '@/utils/useDevice'
import { debounce } from '@/utils/debounce'

const { t } = useI18n()
const { isIOS } = useDevice()

const props = defineProps({
  tag: {
    type: String,
    default: 'Popular',
  },
})

const SysTypeComputed = computed(() => {
  return isIOS.value ? 'ios' : 'android'
})

const buttonList = ref([])
const handleSetList = () => {
  nextTick(() => {
    if (isIOS.value) {
      buttonList.value = [
        {
          id: 1,
          content: t('DownloadPopup-components-InstallButtons.quickApp'),
          isHasTag: true,
          tag: t('DownloadPopup-components-InstallButtons.popular'),
          isHasIcon: false,
          iconType: '',
          isHasRightSlot: true,
          isHasCheckbox: false,
          checked: false,
          checkedContent: '',
          funcType: 'install',
        },
      ]
    } else {
      buttonList.value = [
        {
          id: 1,
          content: t('DownloadPopup-components-InstallButtons.quickAppAndroid'),
          isHasTag: true,
          tag: t('DownloadPopup-components-InstallButtons.popular'),
          isHasIcon: false,
          iconType: '',
          isHasRightSlot: false,
          isHasCheckbox: true,
          checked: true,
          checkedContent: t('DownloadPopup-components-InstallButtons.autoLogin'),
          funcType: 'install',
        },
      ]
    }
  })
}
handleSetList()

const installPrompt = ref(null)

const init = (promptEvent) => {
  installPrompt.value = promptEvent
}

/**
 * 触发PWA安装流程
 */
function triggerInstall() {
  if (installPrompt.value) {
    // 使用之前保存的事件对象来触发安装提示
    installPrompt.value.prompt()

    // 等待用户选择结果
    installPrompt.value.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('用户同意安装')
      } else {
        console.log('用户拒绝安装')
      }
      // 清理保存的事件对象
      installPrompt.value = null
    })
    nextTick(() => {
      emit('download', isIOS.value)
    })
  } else {
    emit('download', isIOS.value)
  }
}
const triggerInstallDebounce = debounce(triggerInstall)

const handleDownload = (type) => {
  switch (type) {
    case 'quick':
      emit('quick')
      break
    case 'install':
      emit('install')
      triggerInstallDebounce()
      break
  }
}

/**
 * 打开教学弹窗
 */
const handleTutorial = () => {
  emit('tutorial')
}

onUnmounted(() => {
  installPrompt.value = null
})

const emit = defineEmits(['close', 'quick', 'install', 'tutorial'])

defineExpose({ init })
</script>

<style scoped lang="scss">
.buttons-container {
  .main-button {
    position: relative;
    margin-top: 5px;
    width: 100%;
    height: 35px;
    line-height: 35px;
    text-align: center;
    background-color: var(--download-popup-cancel-bg);
    border-radius: 6px;
    border: 1px solid;
    border-color: var(--download-popup-cancel-border);
    color: var(--download-popup-cancel-text);

    &.right-slot {
      display: grid;
      grid-template-columns: auto 44px;
    }

    .google-shop-icon-box {
      height: 100%;

      &.ios {
        .icon {
          width: 18px;
          height: 18px;
        }
      }
      &.android {
        .icon {
          width: 19px;
          height: 19px;
        }
      }

      .value-box {
        display: flex;
        align-items: center;
        display: -webkit-box;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: middle;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        line-height: 1.1;

        &.checkbox {
          flex-direction: column;
          justify-content: center;
          gap: 3px;

          .value {
            font-size: 9px;
          }
        }

        .value {
          font-size: 10px;
          word-break: break-all;
        }

        .checkbox-container {
          display: flex;
          justify-content: center;
          align-items: center;
          height: fit-content;
          line-height: 1;
          .text {
            margin-left: 5px;
            font-size: 8px;
          }
        }
      }
    }

    .right-slot {
      position: relative;

      &.link {
        &::after {
          border-color: var(--download-popup-cancel-border);
        }
      }

      &::after {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        content: '';
        height: 70%;
        border-left: 1px solid var(--download-popup-cancel-text);
      }

      .slot-content-box {
        justify-content: center;
        align-items: center;
        gap: 2px;

        .icon {
          width: 14px;
          height: 14px;
        }
        .text {
          font-size: 8px;
          line-height: 1;
        }
      }
    }

    .gift-icon-box {
      position: absolute;
      top: 0px;
      left: -0px;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 30px;
      height: 11px;
      background-color: var(--download-popup-tag-bg);
      border-radius: 5px 5px 5px 0;
      text-align: center;
      color: var(--download-popup-tag-text);
      line-height: 1;

      .value {
        display: inline-block;
        font-size: 6px;
        color: var(--download-popup-gift-tag-text);
      }
    }
  }
}
</style>
